import { defineStore, storeToRefs } from "pinia";
import api from "@/services/api";
import { useAuthStore } from '@/stores';
import type ITenant from "@/models/responses/ITenant";
import CreateTenantRequest from "@/models/requests/CreateTenantRequest";
import UpdateTenantRequest from "@/models/requests/UpdateTenantRequest";
import Tenant from "@/models/frontend/Tenant";

export const useTenantsStore = defineStore("tenants", {
  state: () => ({
    tenants: [] as Tenant[],
    error: "" as any,
    loaded: false,
  }),
  actions: {
    async fetchAll(force = false) {
      const authStore = useAuthStore();

      const { isAuthenticated } = storeToRefs(authStore);

      if (!isAuthenticated.value) return;
      if (!force && this.loaded) return;

      this.tenants = [];
      this.error = "";
      await api
        .get<ITenant[]>("v1.0/tenants")
        .then((response) => {
          this.tenants = response.data.map(this.mapToFrontend);
          this.loaded = true;
        })
        .catch((error) => (this.error = error));
    },
    async create(tenant: CreateTenantRequest) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .post<ITenant>("v1.0/tenants", tenant)
        .then((response) => this.tenants.push(this.mapToFrontend(response.data)))
        .catch((error) => (this.error = error));
    },
    async update(tenantId: number, tenant: UpdateTenantRequest) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .put<ITenant>(`v1.0/tenants/${tenantId}`, tenant)
        .then((response) => {
          const index = this.tenants.findIndex((t) => t.id === tenantId);
          this.tenants[index] = this.mapToFrontend(response.data);
        })
        .catch((error) => (this.error = error));
    },
    async delete(tenantId: number) {
      const authStore = useAuthStore();
      const { isAuthenticated } = storeToRefs(authStore);
      if (!isAuthenticated.value) return;

      this.error = "";
      await api
        .delete(`v1.0/tenants/${tenantId}`)
        .then(() => {
          this.tenants = this.tenants.filter((t) => t.id !== tenantId);
        })
        .catch((error) => (this.error = error));
    },
    mapToFrontend(tenant: ITenant) {
      return new Tenant(tenant.id, tenant.code, tenant.name, tenant.info);
    },
  },
  getters: {
    getById: (state) => (tenantId: number): Tenant => {
      return <Tenant>state.tenants.find((t) => t.id === tenantId);
    },
    getByCode: (state) => (tenantCode: string): Tenant => {
      return <Tenant>state.tenants.find((t) => t.code === tenantCode);
    },
  },
});
